import React, { useEffect, useState } from "react";
import { IconButton } from "@mhome/ui";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { SyncBadge } from "../../common/badges";

const MediaPlayback1Widget = ({ widget, onValueUpdate, pattern }) => {
  const [v0, setV0] = useState({});
  const [playStatus, setPlayStatus] = useState("stopped");
  const [w1, setW1] = useState(null); // play status widget
  const [w2, setW2] = useState(null); // track command widget

  useEffect(() => {
    setV0(widget);

    // Handle play status from $w1 (if present)
    if (widget?.items?.$w1) {
      setW1(widget.items.$w1);
      const status = widget.items.$w1.value;
      if (["playing", "paused", "stopped"].includes(status)) {
        setPlayStatus(status);
      } else {
        setPlayStatus("stopped");
      }
    } else {
      setW1(null);
      setPlayStatus("stopped");
    }

    // Handle track commands from $w2 (if present)
    if (widget?.items?.$w2) {
      setW2(widget.items.$w2);
    } else {
      setW2(null);
    }

    return () => {};
  }, [widget]);

  const sendPlayStatusUpdate = (status) => {
    if (!w1) return; // Don't send update if play status widget doesn't exist

    const updatedWidget = {
      ...v0.items.$w1,
      value: status,
    };
    onValueUpdate(updatedWidget);
  };

  const sendTrackCommandUpdate = (command) => {
    if (!w2) return; // Don't send update if track command widget doesn't exist

    const updatedWidget = {
      ...v0.items.$w2,
      value: command,
    };
    onValueUpdate(updatedWidget);
  };

  const handlePlayPause = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!w1) return; // Don't handle play/pause if play status widget doesn't exist
    if (pattern && pattern.endsWith("_R")) return;

    let newStatus;
    if (playStatus === "playing") {
      newStatus = "paused";
    } else {
      newStatus = "playing";
    }

    setPlayStatus(newStatus);
    sendPlayStatusUpdate(newStatus);
  };

  const handleStop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!w1) return; // Don't handle stop if play status widget doesn't exist
    if (pattern && pattern.endsWith("_R")) return;

    setPlayStatus("stopped");
    sendPlayStatusUpdate("stopped");
  };

  const handlePrevious = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!w2) return; // Don't handle previous if track command widget doesn't exist
    if (pattern && pattern.endsWith("_R")) return;

    sendTrackCommandUpdate("prev");
  };

  const handleNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!w2) return; // Don't handle next if track command widget doesn't exist
    if (pattern && pattern.endsWith("_R")) return;

    sendTrackCommandUpdate("next");
  };

  // Determine which play/pause icon to show
  const getPlayIcon = () => {
    switch (playStatus) {
      case "playing":
        return PauseIcon;
      case "paused":
        return PlayArrowIcon;
      case "stopped":
        return PlayArrowIcon;
      default:
        return PlayArrowIcon;
    }
  };

  const PlayIcon = getPlayIcon();

  return (
    <div
      className="flex items-center justify-center h-full w-full relative gap-2 px-4 overflow-visible"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Previous button - only show if $w2 exists */}
      {w2 && (
        <IconButton
          onClick={handlePrevious}
          className="p-1 hover:bg-[rgba(25,118,210,0.04)]"
          style={{
            color: "hsl(var(--primary))",
            cursor: pattern && pattern.endsWith("_R") ? "default" : "pointer",
            opacity: pattern && pattern.endsWith("_R") ? 0.8 : 1,
          }}
          disabled={pattern && pattern.endsWith("_R")}
        >
          <SkipPreviousIcon />
        </IconButton>
      )}

      {/* Play/Pause button - only show if $w1 exists */}
      {w1 && (
        <IconButton
          onClick={handlePlayPause}
          className={`p-1 ${
            playStatus === "playing"
              ? "hover:bg-[rgba(25,118,210,0.12)]"
              : "hover:bg-[rgba(25,118,210,0.04)]"
          }`}
          style={{
            color: "hsl(var(--primary))",
            cursor: pattern && pattern.endsWith("_R") ? "default" : "pointer",
            opacity: pattern && pattern.endsWith("_R") ? 0.8 : 1,
            backgroundColor:
              playStatus === "playing"
                ? "rgba(25, 118, 210, 0.08)"
                : "transparent",
          }}
          disabled={pattern && pattern.endsWith("_R")}
        >
          <PlayIcon />
        </IconButton>
      )}

      {/* Next button - only show if $w2 exists */}
      {w2 && (
        <IconButton
          onClick={handleNext}
          className="p-1 hover:bg-[rgba(25,118,210,0.04)]"
          style={{
            color: "hsl(var(--primary))",
            cursor: pattern && pattern.endsWith("_R") ? "default" : "pointer",
            opacity: pattern && pattern.endsWith("_R") ? 0.8 : 1,
          }}
          disabled={pattern && pattern.endsWith("_R")}
        >
          <SkipNextIcon />
        </IconButton>
      )}

      {pattern && pattern.endsWith("_W") && <SyncBadge />}
    </div>
  );
};

export default MediaPlayback1Widget;
